import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AppCustomConfigService } from '../../../../core/services/app-custom-config.service';
import { Subscription } from 'rxjs';
import { ViewMode } from '../../../../ech0134/meldungsempfaenger/view-mode.enum';
import { deepCopy } from '../../../../shared/functions/deep-copy.functions';
import { DirtyCheckModalComponent } from '../../../../shared/components/modals/dirty-check-modal/dirty-check-modal.component';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { CapAlertService } from '../../../../core/extension/exceptions/cap-alert.service';
import { DeleteCheckModalComponent } from '../../../../shared/components/modals/delete-check-modal/delete-check-modal.component';
import { LinkEditComponent } from '../edit/link-edit.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { LinkConfigEntry, MenuConfigEntry } from 'app/core/base-api';
import { ComparatorService } from '../../../../shared/services/comparator-service';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RubrikComponent } from '../../../../shared/components/rubrik/rubrik.component';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslateLinkNamePipe } from '../translateLinkName.pipe';

@Component({
  selector: 'cap-link-list',
  templateUrl: './link-list.component.html',
  styleUrls: ['./link-list.component.scss'],
  imports: [
    RouterOutlet,
    RubrikComponent,
    TranslatePipe,
    CdkDropList,
    CdkDrag,
    NgStyle,
    NgTemplateOutlet,
    NgbTooltip,
    TranslateLinkNamePipe
  ]
})
export class LinkListComponent implements OnInit, OnDestroy {
  @Input()
  rootMenuElement: MenuConfigEntry.ElementEnum;

  @Input()
  titleKey: string;

  private viewMode: ViewMode;

  linkEntries: Array<LinkConfigEntry> = new Array<LinkConfigEntry>();
  linkEntriesReadModel: Array<LinkConfigEntry>;

  private subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appCustomConfigService: AppCustomConfigService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private capAlertService: CapAlertService,
    private comparatorService: ComparatorService
  ) {
    this.viewMode = ViewMode.READ_ONLY;
    keysToTranslate<string>(['configuration.linksImMenuUnterstuetzung', 'configuration.linksImMenuUnterstuetzungPublic']);
  }

  ngOnInit(): void {
    this.subscription.add(this.appCustomConfigService.linkConfigSubject.subscribe((links) => this.updateLinks(links)));
  }

  private updateLinks(links) {
    this.linkEntries = links.filter((value) => value.linkRootElement === this.rootMenuElement);
    this.linkEntries.sort((a, b) => this.comparatorService.orderByComparator(a.position, b.position));
    this.linkEntriesReadModel = deepCopy(this.linkEntries);
  }

  drop(event: CdkDragDrop<LinkConfigEntry[]>) {
    moveItemInArray(this.linkEntries, event.previousIndex, event.currentIndex);
  }

  onEdit() {
    this.viewMode = ViewMode.EDIT;
  }

  onSave() {
    const changedEntries: Array<LinkConfigEntry> = [];
    for (const entry of this.linkEntries) {
      const positionBefore = entry.position;
      const positionNow = this.linkEntries.indexOf(entry);
      if (positionBefore !== positionNow) {
        entry.position = positionNow;
        changedEntries.push({ ...entry });
      }
    }
    if (changedEntries) {
      this.appCustomConfigService.updateLinkConfigs(changedEntries).subscribe(() => {
        this.capAlertService.success(this.translate.instant('saveSuccessMessage'));
      });
    }

    this.viewMode = ViewMode.READ_ONLY;
  }

  onCancel() {
    if (this.viewMode === ViewMode.EDIT && this.checkDirty()) {
      this.modalService.open(DirtyCheckModalComponent).result.then(
        () => {
          this.resetModel();
        },
        () => {}
      );
      return;
    }
    this.resetModel();
  }

  onDelete(link: LinkConfigEntry) {
    if (this.viewMode !== ViewMode.READ_ONLY) {
      const modal = this.modalService.open(DeleteCheckModalComponent);
      modal.componentInstance.entity = LinkEditComponent.format(link);
      modal.result.then(
        () => {
          this.subscription.add(
            this.appCustomConfigService.deleteLinkConfig(link).subscribe(() => {
              this.capAlertService.success(
                this.translate.instant('Link') +
                  ' ' +
                  LinkEditComponent.format(link) +
                  ' ' +
                  this.translate.instant('wurdeErfolgreichGeloescht') +
                  '.'
              );
            })
          );
        },
        () => {}
      );
    }
  }

  private resetModel() {
    this.viewMode = ViewMode.READ_ONLY;
    this.linkEntries = deepCopy(this.linkEntriesReadModel);
  }

  onNew() {
    this.router.navigate(['links/new'], {
      relativeTo: this.route,
      queryParams: {
        titleKey: this.titleKey,
        rootMenuElement: this.rootMenuElement
      }
    });
  }

  isEditMode(): boolean {
    return this.viewMode === ViewMode.EDIT;
  }

  isNewMode(): boolean {
    return this.viewMode === ViewMode.NEW;
  }

  isReadOnlyMode(): boolean {
    return this.viewMode === ViewMode.READ_ONLY;
  }

  checkDirty(): boolean {
    const arrVM = this.linkEntries;
    const arrM = this.linkEntriesReadModel;
    if (arrVM.length !== arrM.length) {
      return true;
    }
    for (let i = 0; i < arrVM.length; i++) {
      const entryVmId = arrVM[i].id;
      const entryId = arrM[i].id;
      if (entryVmId !== entryId) {
        return true;
      }
    }
    return false;
  }

  onEditLink(id: string) {
    if (id) {
      this.router.navigate(['links/' + id], {
        relativeTo: this.route,
        queryParams: {
          titleKey: this.titleKey
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
